import React, { useEffect, useState } from 'react';
import { reportHandledError } from '../utils/sentry';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Truck,
  Tag,
  ChevronRight,
  User,
  RefreshCcw,
  Plus,
  ShoppingCart,
  MinusCircle,
  Sun,
  Moon,
  ArrowUpCircle,
  ArrowDownCircle,
  Wallet,
  PieChart,
  Search
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSite } from '../context/SiteContext';
import { getFlowMovements, groupByEntity, formatMoney } from '../utils/flujoFondos';

function FlujoFondosMesDetalle() {
  const { anio, mes } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [isDarkMode] = useState(false);
  const [resumen, setResumen] = useState({ ingresos: [], egresos: [] });
  const [searchText, setSearchText] = useState('');

  // ✅ FIX bug #2 multi-sucursal: leer del SiteContext en vez de localStorage para
  // que el detalle del mes se actualice cuando el usuario cambia de sucursal en el Topbar.
  const { selectedSiteId: contextSiteId } = useSite();
  const selectedSiteId = contextSiteId || null;

  // --- TEMA NOVAERP (IGUAL A FLUJOFONDOS) ---
  const theme = isDarkMode ? {
    bgMain: 'bg-slate-900',
    textHeading: 'text-white',
    textMuted: 'text-slate-400',
    card: 'card-nova bg-slate-800 border-slate-700',
    borderLight: 'border-slate-700'
  } : {
    bgMain: 'bg-[#f4f7fe]',
    textHeading: 'text-[#1b2559]',
    textMuted: 'text-[#a3aed0]',
    card: 'card-nova bg-white border-white',
    borderLight: 'border-slate-100'
  };

  useEffect(() => {
    if (profile?.organization_id) fetchDetalleAcumulado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, anio, mes, selectedSiteId]);

  async function fetchDetalleAcumulado() {
    setLoading(true);
    const orgId = profile.organization_id;
    const monthIndex = Number(mes);
    const startDate = `${anio}-${mes.padStart(2, '0')}-01`;
    const lastDay = new Date(Number(anio), monthIndex, 0).getDate();
    const endDate = `${anio}-${mes.padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    try {
      // Motor único: mismas reglas (dedup, acreditación, compras pendientes,
      // CRM, cuentas por cobrar, paginación) que el resto del módulo. Antes esta
      // pantalla tenía su propio cálculo sin paginar ni deduplicar.
      const movements = await getFlowMovements({
        supabaseClient: supabase,
        organizationId: orgId,
        siteId: selectedSiteId,
        fromDate: startDate,
        toDate: endDate,
      });

      setResumen({
        ingresos: groupByEntity(movements, 'in').map((g) => ({ name: g.name, total: g.total })),
        egresos: groupByEntity(movements, 'out').map((g) => ({ name: g.name, total: g.total })),
      });
    } catch (error) {
      reportHandledError(error, { feature: 'tesoreria:flujo-fondos' });
      toast.error("Error al sincronizar datos");
    } finally {
      setLoading(false);
    }
  }

  // --- COMPONENTES UI ---
  const StatCard = ({ title, value, subtext, subtextColor, icon: Icon, bgIcon, colorIcon }) => (
    <div className={`${theme.card} flex flex-col p-5 h-full justify-between min-w-0 overflow-hidden shadow-sm transition-all hover:-translate-y-1`}>
      <div className={`p-2.5 w-fit rounded-xl ${bgIcon} ${colorIcon} shrink-0 mb-2 shadow-sm`}>
        <Icon size={18} />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest truncate">{title}</p>
        <h3 className={`text-xl lg:text-2xl font-medium ${theme.textHeading} tracking-tight leading-none truncate`}>
          {formatMoney(value)}
        </h3>
        <p className={`text-[9px] font-medium mt-1 truncate ${subtextColor}`}>
          {subtext}
        </p>
      </div>
    </div>
  );

  const totalIn = resumen.ingresos.reduce((acc, i) => acc + i.total, 0);
  const totalOut = resumen.egresos.reduce((acc, e) => acc + e.total, 0);

  const mesNombre = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][mes - 1];

  const q = searchText.trim().toLowerCase();
  const ingresosFiltrados = (resumen.ingresos || []).filter((it) => {
    if (!q) return true;
    return String(it.name || '').toLowerCase().includes(q);
  });
  const egresosFiltrados = (resumen.egresos || []).filter((it) => {
    if (!q) return true;
    return String(it.name || '').toLowerCase().includes(q);
  });

  if (loading) return (
    <div className="h-[70vh] flex flex-col items-center justify-center gap-4 opacity-40">
      <RefreshCcw size={42} className="animate-spin" />
      <p className="text-[10px] font-black uppercase tracking-[0.3em]">Sincronizando flujo mensual...</p>
    </div>
  );

  return (
    <div className="w-full px-6 py-6 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      {/* HEADER (modelo Productos) */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 shadow-sm">
            <PieChart size={22} />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-900 tracking-tighter uppercase leading-none italic">
              Flujo de Fondos · {mesNombre} {anio}
            </h1>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1 italic">
              Distribución mensual de ingresos y egresos{selectedSiteId ? ' · filtrado por sitio' : ''}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => navigate('/sistema/flujo-fondos')}
            className="bg-white border border-slate-200 hover:border-slate-300 text-slate-700 px-4 py-2.5 rounded-xl font-black text-[9px] uppercase tracking-widest shadow-sm transition-all flex items-center gap-2"
          >
            <ArrowLeft size={14} /> Volver
          </button>
          <button
            onClick={fetchDetalleAcumulado}
            className="bg-white border border-slate-200 hover:border-slate-300 text-slate-700 px-4 py-2.5 rounded-xl font-black text-[9px] uppercase tracking-widest shadow-sm transition-all flex items-center gap-2"
            title="Refrescar detalle"
          >
            <RefreshCcw size={14} /> Refrescar
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <StatCard
          title="Ingresos"
          value={totalIn}
          subtext="Total del mes"
          subtextColor="text-[#05cd99]"
          icon={ArrowUpCircle}
          bgIcon="bg-[#05cd99]/10"
          colorIcon="text-[#05cd99]"
        />
        <StatCard
          title="Egresos"
          value={totalOut}
          subtext="Total del mes"
          subtextColor="text-[#ee5d50]"
          icon={ArrowDownCircle}
          bgIcon="bg-[#ee5d50]/10"
          colorIcon="text-[#ee5d50]"
        />
        <StatCard
          title="Saldo Neto"
          value={totalIn - totalOut}
          subtext="Ingresos - Egresos"
          subtextColor="text-slate-400"
          icon={Wallet}
          bgIcon="bg-slate-100"
          colorIcon="text-slate-600"
        />
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between mb-5">
        <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Detalle por entidad</div>
        <div className="relative w-full sm:w-96">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Buscar cliente / proveedor / concepto..."
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-[10px] font-black uppercase tracking-widest placeholder:text-slate-400 outline-none focus:border-[#51A7FD]"
          />
        </div>
      </div>

      {/* COLUMNAS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-[450px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-blue-50/50">
            <h3 className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Ingresos por Cliente</h3>
            <TrendingUp size={16} className="text-blue-400 opacity-40" />
          </div>
          <div className="p-4 space-y-2 overflow-y-auto custom-scrollbar flex-1">
            {ingresosFiltrados.length > 0 ? (
              ingresosFiltrados.map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center p-4 rounded-2xl bg-slate-50/50 hover:bg-slate-50 transition-all border border-transparent hover:border-blue-100"
                >
                  <span className="text-[10px] font-black text-slate-500 uppercase truncate pr-4">{item.name}</span>
                  <span className="text-[11px] font-black text-blue-600 tracking-tight shrink-0">+ {formatMoney(item.total)}</span>
                </div>
              ))
            ) : (
              <div className="h-full flex flex-col items-center justify-center opacity-20 gap-3 py-10">
                <RefreshCcw size={40} />
                <p className="text-[10px] font-black uppercase tracking-widest">Sin datos</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-[450px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-rose-50/50">
            <h3 className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Egresos por Entidad</h3>
            <TrendingDown size={16} className="text-rose-400 opacity-40" />
          </div>
          <div className="p-4 space-y-2 overflow-y-auto custom-scrollbar flex-1">
            {egresosFiltrados.length > 0 ? (
              egresosFiltrados.map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center p-4 rounded-2xl bg-slate-50/50 hover:bg-slate-50 transition-all border border-transparent hover:border-rose-100"
                >
                  <span className="text-[10px] font-black text-slate-500 uppercase truncate pr-4">{item.name}</span>
                  <span className="text-[11px] font-black text-rose-600 tracking-tight shrink-0">- {formatMoney(item.total)}</span>
                </div>
              ))
            ) : (
              <div className="h-full flex flex-col items-center justify-center opacity-20 gap-3 py-10">
                <RefreshCcw size={40} />
                <p className="text-[10px] font-black uppercase tracking-widest">Sin datos</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; }
      `}</style>
    </div>
  );
}

export default FlujoFondosMesDetalle;
